/**
 * Which pitch the end-of-trial card makes this time (src/game/ui/clipOutro.ts).
 *
 * **Why there is more than one.** A purchase is not one decision, it is a few small agreements in
 * a row — *my child is actually reading* · *that price is nothing* · *there is more in there than
 * I have seen*. One card can only ever put one of those in front of a parent, and repeating that
 * same card at the end of every lesson does not add a second: a pitch already read is scenery.
 * So the card rotates. Each showing makes a different case, and a family that browses four
 * lessons has been given four different reasons rather than one reason four times.
 *
 * The order is not arbitrary and is the whole design:
 *
 *  1. **What it is** — the reading claim. It goes first because nothing else matters if the parent
 *     is not sold on what the game does.
 *  2. **What it costs** — once, less than a coffee, and it works with no signal at all. This is
 *     the objection the first card leaves standing, and it is answered second rather than first
 *     because a price quoted before the value reads as a sales pitch.
 *  3. **What is still in there** — the three reward minigames waiting up the planet ladder. Third,
 *     because it is the one that only lands on someone who has already played enough to want more.
 *  4. **Who built it** — a special-needs teacher, real classroom testing, an actual pedagogical
 *     method. Last, and deliberately so: it is the card for the parent still reading at the fourth
 *     lesson, which is the point at which "is this a toy or is it teaching?" is the live question.
 *     Opening with credentials would answer a question nobody had yet.
 *
 * Then it cycles, which is deliberate rather than lazy: a fifth lesson means the family is still
 * playing, and the first case — the reading one — is the one worth making again.
 *
 * Phaser-free on purpose, like src/game/milestones.ts: the rotation is a rule about a counter, and
 * a rule about a counter should be testable without a rendering library (clipOutroVariant.test.ts).
 */

export type OutroVariantId = 'reading' | 'price' | 'rewards' | 'credentials';

export interface OutroVariant {
  id: OutroVariantId;
  /** i18n key for the card's headline. */
  titleKey: string;
  /** i18n keys for the body lines, in order. `clip.spent` takes `{{count}}`; the rest take none. */
  lineKeys: string[];
  /**
   * Offer a QR code instead of the store badge row, where a QR is worth anything — which is
   * desktop web and nowhere else (src/game/qrPromo.ts: nobody scans their own phone). The card
   * decides that, not this table; here it only says the variant would rather have one.
   *
   * On this card it is not a second ask bolted on. The credentials case is the one a parent reads
   * on a laptop while the child plays, and it is the only variant whose whole argument is aimed at
   * someone who will not be buying on the device in front of them.
   */
  prefersQr?: boolean;
}

/**
 * The four cards, in the order they are shown. Adding a fifth is a one-line change here and
 * nowhere else — the rotation is `% OUTRO_VARIANTS.length`.
 */
export const OUTRO_VARIANTS: readonly OutroVariant[] = [
  {
    id: 'reading',
    titleKey: 'clip.title',
    lineKeys: ['clip.spent', 'clip.fluency', 'buy.offline'],
  },
  {
    id: 'price',
    titleKey: 'clip.priceTitle',
    lineKeys: ['buy.coffee', 'buy.once', 'clip.pricePlane'],
  },
  {
    id: 'rewards',
    titleKey: 'clip.rewardsTitle',
    lineKeys: ['clip.rewardsLead', 'clip.rewardsSurprise'],
  },
  {
    id: 'credentials',
    titleKey: 'clip.credTitle',
    lineKeys: ['clip.credTeacher', 'clip.credTested', 'clip.credMethod'],
    prefersQr: true,
  },
] as const;

/**
 * **Three lines each, and that is a budget rather than a style.** The card is drawn at the large
 * size, and it has to fit between the top of the screen and the buttons underneath it — 524 units,
 * see BOTTOM_LIMIT in promoCard.ts. At four lines the reading card is 711 units tall in French and
 * lands squarely on the Replay button; at three it is ~515 wherever it is read.
 *
 * What went, and why it was the right line to lose:
 *  - `clip.second` ("all ten languages") off the reading card. It is a fact about the catalogue,
 *    read at a moment that is about this child and this lesson; the rewards card now carries the
 *    "there is more in here" job on its own.
 *  - `buy.offline` off the rewards card. The trust line is still made once per rotation, on the
 *    reading card, and the price card's own last line already says it needs no signal.
 *
 * Adding a fourth line back means re-measuring in the widest language, not eyeballing it in German.
 *
 * The credentials card spends its three lines on three claims and is the tightest of the four, so
 * it is the one to re-measure first if any of that copy grows. It also carries the QR block, which
 * is taller than the badge row it replaces — laid out side by side for exactly that reason (see
 * src/game/ui/cardQr.ts), but it is still the tallest card in the rotation.
 */

/** How many end-of-trial cards this device has been shown. */
const SHOWN_KEY = 'abcs.clip.outro.v1';

/** The variant for the nth card shown, counting from 0. Wraps. */
export function outroVariantAt(shown: number): OutroVariant {
  const n = Number.isFinite(shown) && shown > 0 ? Math.floor(shown) : 0;
  return OUTRO_VARIANTS[n % OUTRO_VARIANTS.length];
}

function readShown(): number {
  try {
    const n = Number(localStorage.getItem(SHOWN_KEY));
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;   // storage blocked (private mode): every card is the first one, which is the right one
  }
}

/**
 * The next card to show, and the counter moved on.
 *
 * Advancing on SHOW rather than on dismiss is the honest reading of "they have seen this one" —
 * a card the parent ignored is still a card that has had its turn, and re-showing it would spend
 * the next lesson repeating an argument that already failed.
 */
export function nextOutroVariant(): OutroVariant {
  const shown = readShown();
  try {
    localStorage.setItem(SHOWN_KEY, String(shown + 1));
  } catch { /* storage blocked — the rotation simply restarts, which is better than nothing */ }
  return outroVariantAt(shown);
}

/** Test/dev affordance: start the rotation over. Nothing in the app calls this. */
export function resetOutroVariants(): void {
  try {
    localStorage.removeItem(SHOWN_KEY);
  } catch { /* nothing to do */ }
}
